/**
 * Material Request Note List
 * Material requests for inventory items, raised by a cost center. From here they can be created,
 * edited/deleted (while draft), submitted, released (recording inventory consumption),
 * and rejected. Releasing is what records consumption against the request.
 */

import React, { useState, useEffect, useRef } from 'react';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import PermissionGuard from '../../Common/PermissionGuard';
import ConfirmationModal from '../../Common/ConfirmationModal';
import MaterialRequestNoteForm from './MaterialRequestNoteForm';
import ReleaseMaterialModal from './ReleaseMaterialModal';
import inventoryApi from '../../../services/inventoryApi';
import toast from 'react-hot-toast';

const STATUS_LABELS = {
    draft: 'Draft',
    pending: 'Pending',
    partially_released: 'Partially Released',
    completed: 'Completed',
    rejected: 'Rejected',
    cancelled: 'Cancelled'
};

const MaterialRequestNoteList = () => {
    const [mrns, setMrns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const searchTimeoutRef = useRef(null);

    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [releasing, setReleasing] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmComment, setConfirmComment] = useState('');
    const [confirmLoading, setConfirmLoading] = useState(false);

    // After an action: close the dialogs (and the view popup it may have come from) and reload
    const afterAction = () => {
        setConfirmAction(null);
        setConfirmComment('');
        setViewing(null);
        loadMrns();
    };

    const [view, setView] = useState(() => {
        try {
            return localStorage.getItem('mrnListView') || 'card';
        } catch {
            return 'card';
        }
    });

    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
            setPagination(prev => ({ ...prev, page: 1 }));
        }, 500);
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [searchInput]);

    useEffect(() => {
        loadMrns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchInput, statusFilter, pagination.page, pagination.limit]);

    const loadMrns = async () => {
        try {
            setLoading(true);
            const params = { page: pagination.page, limit: pagination.limit };
            if (debouncedSearchInput) params.search = debouncedSearchInput;
            if (statusFilter) params.status = statusFilter;

            const result = await inventoryApi.getMaterialRequestNotes({ ...params, sortBy: 'createdAt', sortOrder: 'desc' });
            setMrns(result.data || []);
            if (result.pagination) {
                setPagination({
                    page: result.pagination.page || 1,
                    limit: result.pagination.limit || 20,
                    total: result.pagination.total || 0,
                    pages: result.pagination.pages || 0
                });
            }
        } catch (err) {
            console.error('Error loading material request notes:', err);
            toast.error(err.response?.data?.message || err.message || 'Failed to load material request notes');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => setPagination(prev => ({ ...prev, page: newPage }));
    const handleLimitChange = (newLimit) => setPagination(prev => ({ page: 1, limit: newLimit, total: prev.total, pages: prev.pages }));

    const handleViewChange = (newView) => {
        setView(newView);
        try {
            localStorage.setItem('mrnListView', newView);
        } catch (error) {
            console.warn('Failed to save view preference:', error);
        }
    };

    const clearFilters = () => {
        setSearchInput('');
        setStatusFilter('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const hasFilters = () => searchInput || statusFilter;

    const handleDelete = (mrn) => {
        setConfirmAction({
            type: 'danger',
            title: 'Delete Material Request Note',
            message: `Delete draft "${mrn.mrnNo}"? This cannot be undone.`,
            confirmText: 'Delete',
            onConfirm: async () => {
                try {
                    await inventoryApi.deleteMaterialRequestNote(mrn._id);
                    toast.success('Material request note deleted');
                    afterAction();
                } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to delete');
                }
            }
        });
    };

    const handleSubmit = (mrn) => {
        setConfirmAction({
            type: 'success',
            title: 'Submit Material Request Note',
            message: `Submit "${mrn.mrnNo}"? You won't be able to edit it once submitted.`,
            confirmText: 'Submit',
            onConfirm: async () => {
                try {
                    await inventoryApi.submitMaterialRequestNote(mrn._id);
                    toast.success('Material request note submitted');
                    afterAction();
                } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to submit');
                }
            }
        });
    };

    const handleReject = (mrn) => {
        setConfirmAction({
            type: 'danger',
            title: 'Reject Material Request Note',
            message: `Reject "${mrn.mrnNo}"? Please provide a reason.`,
            confirmText: 'Reject',
            showCommentBox: true,
            commentRequired: true,
            commentPlaceholder: 'Rejection reason (required)...',
            onConfirm: async (reason) => {
                if (!String(reason || '').trim()) { toast.error('Rejection reason is required'); return; }
                try {
                    await inventoryApi.rejectMaterialRequestNote(mrn._id, String(reason).trim());
                    toast.success('Material request note rejected');
                    afterAction();
                } catch (err) {
                    toast.error(err.response?.data?.message || 'Failed to reject');
                }
            }
        });
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'draft': return 'bg-gray-100 text-gray-700 border-gray-300';
            case 'pending': return 'bg-orange-100 text-orange-700 border-orange-300';
            case 'partially_released': return 'bg-blue-100 text-blue-700 border-blue-300';
            case 'completed': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
            case 'rejected': return 'bg-red-100 text-red-700 border-red-300';
            default: return 'bg-gray-100 text-gray-600 border-gray-300';
        }
    };

    const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'partially_released', label: 'Partially Released' },
        { value: 'completed', label: 'Completed' },
        { value: 'rejected', label: 'Rejected' }
    ];

    const canRelease = (status) => status === 'pending' || status === 'partially_released';

    const renderActions = (mrn, size = 'sm') => {
        const iconCls = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
        return (
            <>
                <button onClick={() => setViewing(mrn)} className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all" title="View">
                    <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                </button>
                {mrn.status === 'draft' && (
                    <>
                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:UPDATE">
                            <button onClick={() => setEditing(mrn)} className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all" title="Edit">
                                <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                            </button>
                        </PermissionGuard>
                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:SUBMIT">
                            <button onClick={() => handleSubmit(mrn)} className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all" title="Submit">
                                <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                            </button>
                        </PermissionGuard>
                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:DELETE">
                            <button onClick={() => handleDelete(mrn)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all" title="Delete">
                                <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </PermissionGuard>
                    </>
                )}
                {canRelease(mrn.status) && (
                    <>
                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:RELEASE">
                            <button onClick={() => setReleasing(mrn)} className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-all" title="Release Materials">
                                <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M13 6l6 6-6 6" />
                                </svg>
                            </button>
                        </PermissionGuard>
                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:REJECT">
                            <button onClick={() => handleReject(mrn)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all" title="Reject">
                                <svg className={iconCls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </PermissionGuard>
                    </>
                )}
            </>
        );
    };

    const renderMrnCard = (mrn) => (
        <div key={mrn._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group w-full">
            <div className="p-3 flex flex-col gap-2">
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                            {mrn.mrnNo?.slice(-2) || 'MR'}
                        </div>
                        <div className="min-w-0 flex-1">
                            <button
                                onClick={() => setViewing(mrn)}
                                className="text-sm font-semibold text-gray-900 hover:text-indigo-600 transition-colors truncate text-left block"
                            >
                                {mrn.mrnNo}
                            </button>
                            <p className="text-xs text-gray-400">{mrn.requestDate ? new Date(mrn.requestDate).toLocaleDateString() : ''}</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                        {renderActions(mrn, 'sm')}
                    </div>
                </div>

                {/* Cost center + priority */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="truncate">{mrn.costCenterName || '—'}</span>
                    {mrn.priority === 'urgent' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 border border-red-300 flex-shrink-0 ml-2">Urgent</span>
                    )}
                </div>

                {/* Items + Status */}
                <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                    <span className="text-xs text-gray-600 truncate">
                        {mrn.lineItems?.length || 0} items{mrn.requestWarehouseName ? ` • ${mrn.requestWarehouseName}` : ''}
                    </span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border flex-shrink-0 ml-2 ${getStatusBadgeColor(mrn.status)}`}>
                        {STATUS_LABELS[mrn.status] || mrn.status}
                    </span>
                </div>
            </div>
        </div>
    );

    const renderMrnRow = (mrn) => (
        <tr key={mrn._id} className="hover:bg-gray-50 transition-colors">
            <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center space-x-3">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                        {mrn.mrnNo?.slice(-2) || 'MR'}
                    </div>
                    <div>
                        <button onClick={() => setViewing(mrn)} className="text-sm font-semibold text-gray-900 hover:text-indigo-600">
                            {mrn.mrnNo}
                        </button>
                        <div className="text-xs text-gray-400">{mrn.requestDate ? new Date(mrn.requestDate).toLocaleDateString() : ''}</div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{mrn.costCenterName || '—'}</td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{mrn.requestWarehouseName || '—'}</td>
            <td className="px-4 py-3 whitespace-nowrap">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusBadgeColor(mrn.status)}`}>
                    {STATUS_LABELS[mrn.status] || mrn.status}
                </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{mrn.lineItems?.length || 0} items</td>
            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-1">
                    {renderActions(mrn, 'md')}
                </div>
            </td>
        </tr>
    );

    if (loading && mrns.length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <div className="bg-white shadow-sm border border-gray-200">
            {error && (
                <div className="mx-4 mt-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
            )}

            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                    {/* Search */}
                    <div className="flex-1">
                        <TextInput
                            variant="input"
                            type="text"
                            placeholder="Search by MRN number, cost center, items..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            icon={({ className }) => (
                                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            )}
                            className="text-sm py-1.5 !bg-white border border-gray-300 shadow-sm !rounded-sm"
                        />
                    </div>

                    {/* Filters, View Toggle, New button */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        <CustomSelect
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            options={statusOptions}
                            size="sm"
                            variant="default"
                            className="min-w-[170px]"
                        />

                        {hasFilters() && (
                            <button
                                onClick={clearFilters}
                                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                                title="Clear all filters"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}

                        {/* View Toggle */}
                        <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                            <button
                                onClick={() => handleViewChange('card')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                                title="Card view"
                            >
                                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                                </svg>
                                Cards
                            </button>
                            <button
                                onClick={() => handleViewChange('table')}
                                className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'table' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'}`}
                                title="List view"
                            >
                                <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                </svg>
                                List
                            </button>
                        </div>

                        {/* New MRN Button */}
                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:CREATE">
                            <button
                                onClick={() => setCreating(true)}
                                className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200 whitespace-nowrap"
                            >
                                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                New Request
                            </button>
                        </PermissionGuard>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
                <div className="flex items-center justify-between px-2 py-2 bg-gradient-to-r from-gray-100 to-gray-100 border-b border-gray-200">
                    {/* Results Info */}
                    <div className="flex items-center gap-2">
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-gray-900">
                                {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} requests
                            </p>
                            <p className="text-[10px] text-gray-500">
                                {hasFilters() ? 'Filtered results' : 'All material requests'}
                            </p>
                        </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 font-medium">Show:</span>
                            <CustomSelect
                                value={pagination.limit}
                                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                                disabled={loading}
                                options={[
                                    { value: 10, label: '10' },
                                    { value: 20, label: '20' },
                                    { value: 50, label: '50' },
                                    { value: 100, label: '100' }
                                ]}
                                size="sm"
                                variant="minimal"
                                className="min-w-[70px]"
                            />
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.page <= 1 || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="First page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                                disabled={pagination.page <= 1 || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span className="hidden sm:inline">Previous</span>
                            </button>

                            <div className="hidden md:flex items-center gap-1">
                                {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                                    const pageNum = i + 1;
                                    const isCurrentPage = pageNum === pagination.page;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            disabled={loading}
                                            className={`min-w-[28px] px-2 py-1.5 rounded text-xs font-medium transition-all ${isCurrentPage ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'}`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                {pagination.pages > 7 && <span className="px-2 text-gray-400 text-xs">...</span>}
                            </div>

                            <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                                <span className="text-xs font-medium text-gray-900">{pagination.page}</span>
                                <span className="text-xs text-gray-500 mx-1">of</span>
                                <span className="text-xs font-medium text-gray-900">{pagination.pages}</span>
                            </div>

                            <button
                                onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-xs"
                            >
                                <span className="hidden sm:inline">Next</span>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                            <button
                                onClick={() => handlePageChange(pagination.pages)}
                                disabled={pagination.page >= pagination.pages || loading}
                                className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                title="Last page"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="p-2">
                {mrns.length === 0 ? (
                    <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No material request notes found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {hasFilters() ? 'Try adjusting your search or filters.' : 'Create a new request to get materials for your cost center.'}
                        </p>
                    </div>
                ) : (
                    <>
                        {view === 'card' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {mrns.map(renderMrnCard)}
                            </div>
                        )}
                        {view === 'table' && (
                            <div className="overflow-x-auto">
                                <table className="min-w-max w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">MRN</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Cost Center</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Warehouse</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Items</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {mrns.map(renderMrnRow)}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Create / Edit form */}
            {creating && <MaterialRequestNoteForm onClose={() => setCreating(false)} onSuccess={loadMrns} />}
            {editing && <MaterialRequestNoteForm mrn={editing} onClose={() => setEditing(null)} onSuccess={loadMrns} />}

            {/* Release modal */}
            {releasing && <ReleaseMaterialModal mrn={releasing} onClose={() => setReleasing(null)} onSuccess={loadMrns} />}

            {/* View modal */}
            {viewing && (
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
                    <div className="flex items-center justify-center min-h-full px-4 py-6">
                        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
                            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5 flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-white">{viewing.mrnNo}</h3>
                                <button onClick={() => setViewing(null)} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="px-4 py-3">
                                <div className="grid grid-cols-2 gap-2 mb-3 text-xs text-gray-600">
                                    <div><span className="font-medium text-gray-500">Cost Center: </span>{viewing.costCenterName || '—'}</div>
                                    <div>
                                        <span className="font-medium text-gray-500">Status: </span>
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusBadgeColor(viewing.status)}`}>{STATUS_LABELS[viewing.status] || viewing.status}</span>
                                    </div>
                                    <div><span className="font-medium text-gray-500">Request Date: </span>{viewing.requestDate ? new Date(viewing.requestDate).toLocaleDateString() : '—'}</div>
                                    <div><span className="font-medium text-gray-500">Items: </span>{viewing.lineItems?.length || 0}</div>
                                    <div><span className="font-medium text-gray-500">Warehouse: </span>{viewing.requestWarehouseName || '—'}</div>
                                    <div><span className="font-medium text-gray-500">Priority: </span>{viewing.priority}</div>
                                    {viewing.status === 'rejected' && viewing.rejectionReason && (
                                        <div className="col-span-2 text-red-600"><span className="font-medium">Rejection reason: </span>{viewing.rejectionReason}</div>
                                    )}
                                </div>
                                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                                    <table className="min-w-max w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Item</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">UoM</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Requested</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Released</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {(viewing.lineItems || []).map((line, index) => (
                                                <tr key={index}>
                                                    <td className="px-2 py-1.5">
                                                        <div className="text-xs font-medium text-gray-900">{line.itemDescription}</div>
                                                        <div className="text-xs text-gray-500 font-mono">{line.itemCode}</div>
                                                    </td>
                                                    <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.uom || '—'}</td>
                                                    <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.quantityRequested}</td>
                                                    <td className="px-2 py-1.5 text-center text-xs font-medium text-gray-900">{line.quantityReleased || 0}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {viewing.notes && (
                                    <p className="mt-3 text-xs text-gray-600"><span className="font-medium text-gray-500">Notes: </span>{viewing.notes}</p>
                                )}
                            </div>

                            {/* Actions for this request's status */}
                            <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                                {viewing.status === 'draft' && (
                                    <>
                                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:DELETE">
                                            <button onClick={() => handleDelete(viewing)}
                                                className="px-3 py-1.5 text-xs font-medium border border-red-300 text-red-700 bg-white hover:bg-red-50 rounded">
                                                Delete
                                            </button>
                                        </PermissionGuard>
                                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:UPDATE">
                                            <button onClick={() => { setEditing(viewing); setViewing(null); }}
                                                className="px-3 py-1.5 text-xs font-medium border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 rounded">
                                                Edit
                                            </button>
                                        </PermissionGuard>
                                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:SUBMIT">
                                            <button onClick={() => handleSubmit(viewing)}
                                                className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded">
                                                Submit
                                            </button>
                                        </PermissionGuard>
                                    </>
                                )}
                                {canRelease(viewing.status) && (
                                    <>
                                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:REJECT">
                                            <button onClick={() => handleReject(viewing)}
                                                className="px-3 py-1.5 text-xs font-medium border border-red-300 text-red-700 bg-white hover:bg-red-50 rounded">
                                                Reject
                                            </button>
                                        </PermissionGuard>
                                        <PermissionGuard permission="INVENTORY:MATERIAL_REQUEST_NOTES:RELEASE">
                                            <button onClick={() => { setReleasing(viewing); setViewing(null); }}
                                                className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded">
                                                Release Materials
                                            </button>
                                        </PermissionGuard>
                                    </>
                                )}
                                <button onClick={() => setViewing(null)}
                                    className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation modal */}
            {confirmAction && (
                <ConfirmationModal
                    isOpen={!!confirmAction}
                    onClose={() => { setConfirmAction(null); setConfirmComment(''); }}
                    onConfirm={async (comment) => {
                        setConfirmLoading(true);
                        try { await confirmAction.onConfirm(comment); } finally { setConfirmLoading(false); }
                    }}
                    title={confirmAction.title}
                    message={confirmAction.message}
                    confirmText={confirmAction.confirmText}
                    type={confirmAction.type}
                    loading={confirmLoading}
                    showCommentBox={!!confirmAction.showCommentBox}
                    comment={confirmComment}
                    onCommentChange={setConfirmComment}
                    commentPlaceholder={confirmAction.commentPlaceholder}
                    disableConfirm={!!confirmAction.commentRequired && !confirmComment.trim()}
                    overlayClassName="z-[60]"
                />
            )}
        </div>
    );
};

export default MaterialRequestNoteList;
